import { EditorV3Content, isIEditorV3 } from "../classes";

/**
 * Checks if the provided EditorV3 content contains any text blocks with styles
 * that should be removed or cleaned, as defined in the STYLES_TO_CLEAN (Optional, OptionalLocked)
 * and STYLES_TO_DELETE (Notes) configuration constants.
 *
 * This function is used to determine whether content needs style processing, particularly
 * when deciding whether to apply external single replacements. It checks for both:
 * - Styles that will be removed (from STYLES_TO_DELETE, such as Notes)
 * - Styles that will be converted to black text (from STYLES_TO_CLEAN, such as Optional, OptionalLocked)
 *
 * The generic constraint ensures this function works with any EditorV3-compatible content type,
 * maintaining type consistency with calling functions that use generic table data structures.
 *
 * @template T - Content type extending IEditorV3, used in generic table structures
 * @param content - EditorV3 content to check for removable or cleanable styles
 * @param styleList - Array of style names to check against (typically STYLES_TO_CLEAN and STYLES_TO_DELETE)
 * @returns True if any text block uses a style listed in STYLES_TO_CLEAN or STYLES_TO_DELETE
 *          (for example Notes, Optional, or OptionalLocked); false if no such styles are found
 *          or if all blocks lack style properties
 */
export const hasStylesToRemove = <T>(content: T, styleList: string[]): boolean => {
  if (!isIEditorV3(content)) {
    return false;
  }

  const hasStyles = new EditorV3Content(content).hasStyles;
  return hasStyles.some((s) => styleList.includes(s));
};
